import React, { useState } from "react";
import { Chip } from "@mui/material";
import CustomTable from "../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import { useAllCountriesViewModel } from "./AllCountriesViewModel";
import AddCountry from "../ManageCountry/AddCountry";
import PublicIcon from "@mui/icons-material/Public";
import { useLoading } from "../../contexts/LoadingContext";
import Modal from "../../components/Modal/Modal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import CircleIcon from "@mui/icons-material/Circle";
import DeleteIcon from "@mui/icons-material/Delete";
import { useRbac } from "../../hooks/RbacContext";


const AllCountries = () => {
  const { countries, handleDelete, createCountry, updateCountry } =
    useAllCountriesViewModel();
  const { showLoading, hideLoading } = useLoading();
  const [isAddCountryOpen, setIsAddCountryOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const { checkAccess } = useRbac();

  const columns = [
    {
      id: "country_name",
      label: "Name",
    },
    {
      id: "country_code",
      label: "Code",
      align: "left",
    },
    {
      id: "short_name",
      label: "Short Name",
      align: "left",
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
    {
      id: "is_active",
      label: "Status",
      align: "left",
      render: (value) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            backgroundColor: value ? "#E8F5E9" : "#FFF3E0",
            color: value ? "#2E7D32" : "#EF6C00",
            "& .MuiChip-label": {
              px: 1.5,
            },
          }}
        />
      ),
      searchValue: (value) => (value ? "active" : "inactive"),
    },
  ];

  const fixedColumns = {
    left: [],
    right: ["is_active", "actions"],
  };

  const handleAddCountry = () => {
    setSelectedCountry(null);
    setIsAddCountryOpen(true);
  };

  const handleEditCountry = (country) => {
    setSelectedCountry(country);
    setIsAddCountryOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddCountryOpen(false);
    setSelectedCountry(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedCountry) {
        await updateCountry(selectedCountry.country_id, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Country updated successfully",
        });
      } else {
        await createCountry(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Country added successfully",
        });
      }
      handleCloseDialog();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (country) => {
    setDeleteConfirmation({
      country,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const country = deleteConfirmation.country;
    try {
      showLoading();
      await handleDelete(country);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Country deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete country",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (country) => {
    const actions = [];
    if (checkAccess("country", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditCountry,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("country", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  const columnFilters = [
    {
      id: "country_name",
      type: "select",
      label: "Country",
      icon: <PublicIcon />,
      sendIcon: true,
    },
    {
      id: "is_active",
      label: "Status",
      align: "left",
      render: (value) => (value ? "Active" : "Inactive"),
      searchValue: (value) => (value ? "active" : "inactive"),
      icon: <CircleIcon />
    },
  ];

  return (
    <div>
      <CustomTable
        columns={columns}
        data={countries}
        onAdd={handleAddCountry}
        showActions={true}
        showAddButton={checkAccess("country", "add")}
        addButtonIcon={<PublicIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Countries"
        subtitle="Manage and monitor all countries"
        addButtonText="Add Country"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddCountry
        open={isAddCountryOpen}
        onClose={handleCloseDialog}
        initialData={selectedCountry}
        onSubmit={handleSubmit}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Country"
          message={`Are you sure you want to delete country "${deleteConfirmation.country.country_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default AllCountries;
